import type { ObjectiveOptionGroups } from '@/state/experiments/objective-options';
import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from 'helpers/cn';
import { DEFAULT_PRIMARY_OBJECTIVE } from 'helpers/objectives';
import { isUnmapped } from '@/state/experiments/objective-options';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ObjectiveCommandGroups } from './ObjectiveCommandGroups';
import { EventTag } from './EventTag';

type PrimaryObjectivePickerProps = {
  /** experiments.primary_goal: null is the website default. */
  value: string | null;
  /** The website default's event; null when nothing is mapped anywhere and purchase is assumed. */
  defaultEvent: string | null;
  groups: ObjectiveOptionGroups;
  onChange: (next: string | null) => void;
  /** Read-only: the experiment is running and its objectives are locked. */
  disabled?: boolean;
};

/**
 * The primary objective as a full-width searchable select: the website default, then the events
 * mapped as primary, as secondary, and not mapped. Searchable because the not-mapped group is every
 * event the website has sent. The trigger names what is measured, so the card needs no
 * "Measuring X" line under it.
 */
export const PrimaryObjectivePicker = ({
  value,
  defaultEvent,
  groups,
  onChange,
  disabled = false,
}: PrimaryObjectivePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const defaultTag = defaultEvent ?? `${DEFAULT_PRIMARY_OBJECTIVE}, assumed`;
  const defaultLabel = `Website default (${defaultTag})`;

  const pick = (next: string | null) => {
    setIsOpen(false);
    if (next !== value) onChange(next);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          aria-label="Primary objective"
          disabled={disabled}
          className="border-line bg-panel text-foreground h-10 w-full max-w-[560px] justify-start gap-2.5 rounded-lg px-3.5 font-normal"
        >
          {value === null ? (
            <>
              <span className="flex-1 truncate text-left text-body">Website default</span>
              <EventTag>{defaultTag}</EventTag>
            </>
          ) : (
            <>
              <span className="flex-1 truncate text-left font-mono text-meta">{value}</span>
              {isUnmapped(groups, value) ? <EventTag tone="muted">not mapped</EventTag> : null}
            </>
          )}
          <ChevronsUpDown className="text-dim size-4 shrink-0" strokeWidth={1.75} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] min-w-[280px] p-0"
      >
        <Command>
          <CommandInput placeholder="Search events" className="text-body" />
          <CommandList>
            <CommandEmpty className="supporting px-3 py-4">No matching events.</CommandEmpty>
            <CommandGroup>
              <CommandItem value={defaultLabel} onSelect={() => pick(null)}>
                <Check className={cn('size-4 shrink-0', value === null ? 'opacity-100' : 'opacity-0')} />
                <span className="flex-1">Website default</span>
                <EventTag>{defaultTag}</EventTag>
              </CommandItem>
            </CommandGroup>
            <ObjectiveCommandGroups
              selected={value}
              onPick={pick}
              sections={[
                { heading: 'Mapped as primary', names: groups.primary },
                { heading: 'Mapped as secondary', names: groups.secondary },
                { heading: 'Not mapped', names: groups.notMapped },
                { heading: "Not among this website's events", names: groups.untracked },
              ]}
            />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
